
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/HeaderUpdated';
import Footer from '../components/Footer';
import { getProductById } from '../services/productService';
import { Product } from '../types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/components/ui/use-toast';
import { useFavorites } from '@/hooks/useFavorites';

const ProductPage = () => {
  const { id } = useParams<{ id: string }>();
  const productId = id as string;
  const [product, setProduct] = useState<Product | null>(null);
  const { toast } = useToast();
  const { addToCart } = useCart();
  const [loading, setLoading] = useState(true);
  const [isFav, setIsFav] = useState(false);
  const { favorites, toggleFavorite } = useFavorites();
  const userId = localStorage.getItem('user_id');
  const [selectedImage, setSelectedImage] = useState<string>('');

  useEffect(() => {
    const loadProduct = async () => {
      if (productId) {
        try {
          const productData = await getProductById(productId);
          setProduct(productData);
          setSelectedImage(productData.image); // Set initial selected image
        } catch (error) {
          console.error("Erro ao carregar produto:", error);
          toast({
            title: "Erro",
            description: "Não foi possível carregar o produto.",
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
      }
    };

    loadProduct();
  }, [productId]);

  useEffect(() => {
    if (userId && productId) {
      setIsFav(favorites.includes(productId));
    }
  }, [userId, productId, favorites]);

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (!product) {
    return <div>Produto não encontrado</div>;
  }

  const handleAddToCart = () => {
    addToCart(product, 1);
    toast({
      title: "Sucesso",
      description: "Produto adicionado ao carrinho!",
    });
  };

  const handleAddToFavorites = async () => {
    if (!userId) {
      toast({
        title: "Erro",
        description: "Você precisa estar logado para adicionar aos favoritos",
        variant: "destructive",
      });
      return;
    }

    try {
      await toggleFavorite(productId);
      setIsFav(!isFav);
    } catch (error: any) {
      toast({
        title: "Erro",
        description: error.message || "Erro ao atualizar favoritos",
        variant: "destructive",
      });
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-brand-darker text-white">
      <Header />
      <main className="flex-grow py-4 md:py-12 px-3 md:px-4">
        <div className="container mx-auto">
          <Card className="bg-brand-gray border-none text-white">
            <CardContent className="p-3 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                {/* Product Image Gallery */}
                <div className="space-y-3 md:space-y-4">
                  {/* Main Image */}
                  <div className="bg-white rounded-lg p-3 md:p-4">
                    <img
                      src={selectedImage || product.image}
                      alt={product.name}
                      className="w-full h-auto object-contain max-h-[300px] md:max-h-[500px] rounded-md"
                    />
                  </div>

                  {/* Thumbnail Gallery - Horizontal Scroll */}
                  {product.additionalImages && product.additionalImages.length > 0 && (
                    <div className="relative">
                      <div className="flex gap-2 overflow-x-auto pb-2 scroll-smooth snap-x snap-mandatory scrollbar-thin scrollbar-thumb-brand-red scrollbar-track-gray-700">
                        {/* Main image thumbnail */}
                        <button
                          onClick={() => setSelectedImage(product.image)}
                          className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 bg-white rounded-lg p-2 border-2 transition-all snap-start ${
                            selectedImage === product.image
                              ? 'border-brand-red ring-2 ring-brand-red/50'
                              : 'border-gray-300 hover:border-brand-red/50'
                          }`}
                        >
                          <img
                            src={product.image}
                            alt={`${product.name} - Main`}
                            className="w-full h-full object-contain"
                          />
                        </button>

                        {/* Additional images thumbnails */}
                        {product.additionalImages.map((img, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedImage(img)}
                            className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 bg-white rounded-lg p-2 border-2 transition-all snap-start ${
                              selectedImage === img
                                ? 'border-brand-red ring-2 ring-brand-red/50'
                                : 'border-gray-300 hover:border-brand-red/50'
                            }`}
                          >
                            <img
                              src={img}
                              alt={`${product.name} - ${index + 1}`}
                              className="w-full h-full object-contain"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="space-y-3 md:space-y-4">
                  {/* Title */}
                  <h1 className="text-xl md:text-3xl font-bold leading-tight">{product.name}</h1>

                  {/* Description */}
                  <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                    {product.description}
                  </p>

                  {/* Price */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between py-3 md:py-4 border-t border-b border-gray-700">
                    <div className="mb-3 md:mb-0">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-2xl md:text-3xl font-bold text-brand-red">
                          {product.pointsPrice}
                        </span>
                        <span className="text-sm md:text-base text-brand-red font-medium">
                          pontos
                        </span>
                      </div>
                      {product.price && product.price > 0 && (
                        <span className="text-xs md:text-sm text-gray-500 line-through">
                          €{product.price.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons - Mobile Optimized */}
                  <div className="space-y-2 md:space-y-3">
                    <Button
                      onClick={handleAddToCart}
                      className="w-full bg-brand-red hover:bg-brand-red/90 text-white font-semibold h-11 md:h-12 text-sm md:text-base"
                    >
                      Adicionar ao Carrinho
                    </Button>

                    <Button
                      variant="outline"
                      onClick={handleAddToFavorites}
                      className="w-full border-2 border-gray-600 text-white hover:bg-gray-700 h-11 md:h-12 text-sm md:text-base"
                    >
                      {isFav ? "Remover dos Favoritos" : "Adicionar aos Favoritos"}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProductPage;
